<script setup lang="ts">
import {
  defineProps,
  PropType,
  onMounted,
  computed,
  ref,
  onBeforeUnmount
} from 'vue'
import AspectRatioContainer from '@base/components/AspectRatioContainer/AspectRatioContainer.vue'
import { IntersectionObserverWrapper } from '@base/helpers/IntersectionObserverWrapper'
import { PageVisibilityWrapper } from '@base/helpers/PageVisibilityWrapper'
import { VideoSource } from '@base/_types/Video'

const props = defineProps({
  // --- HTML attributes ---
  autoplay: {
    type: Boolean,
    default: true
  },
  controls: {
    type: Boolean,
    default: false
  },
  height: {
    type: String,
    default: ''
  },
  loop: {
    type: Boolean,
    default: true
  },
  muted: {
    type: Boolean,
    default: true
  },
  playsinline: {
    type: Boolean,
    default: true
  },
  poster: {
    type: String,
    default: ''
  },
  preload: {
    type: String,
    default: 'auto',
    validator: (value: string) => {
      return ['auto', 'none', 'metadata'].includes(value)
    }
  },
  sources: {
    type: Array as PropType<VideoSource[]>,
    default: () => []
  },
  src: {
    type: String,
    default: ''
  },
  width: {
    type: String,
    default: ''
  },

  // --- Non-HTML attributes ---
  aspectRatio: {
    type: String,
    default: '16-9',
    validator: (value: string) => {
      return ['16-9', '9-16', '4-3', '3-4', '1'].includes(value)
    }
  },
  lazyAutoplay: {
    // `autoplay` has to be `true` for this to have an effect
    type: Boolean,
    default: true
  },
  objectFit: {
    type: String,
    default: 'cover',
    validator: (value: string) => {
      return ['contain', 'cover', 'fill', 'none', 'scale-down'].includes(value)
    }
  },
  objectPosition: {
    type: String,
    default: 'center center'
  }
})

let videoRef = ref<HTMLVideoElement>()
let intersectionObserver: IntersectionObserverWrapper | null = null
let pageVisibilityWrapper: PageVisibilityWrapper | null = null
let videoVisible: boolean = false
let playing = false
let browserIsWindowsEdge = ref(false)

onMounted(() => {
  if (!videoRef.value) return

  checkBrowser()

  videoRef.value.addEventListener('playing', setPlayingAsTrue)
  videoRef.value.addEventListener('ended', setPlayingAsFalse)
  videoRef.value.addEventListener('pause', setPlayingAsFalse)

  lazyAutoplayInit()
})

onBeforeUnmount(() => {
  if (!videoRef.value) return

  videoRef.value.removeEventListener('playing', setPlayingAsTrue)
  videoRef.value.removeEventListener('ended', setPlayingAsFalse)
  videoRef.value.removeEventListener('pause', setPlayingAsFalse)

  lazyAutoplayCleanup()
})

const usesAspectRatio = () => {
  return props.height === ''
}

const computedWrapperTag = computed(() => {
  return usesAspectRatio() ? AspectRatioContainer : 'div'
})

const computedAspectRatio = computed(() => {
  return usesAspectRatio() ? props.aspectRatio : undefined
})

const computedWrapperModifierClass = computed(() => {
  return usesAspectRatio()
    ? 'video-wrapper--variableHeight'
    : 'video-wrapper--fixedHeight'
})

const computedSources = computed(() => {
  /*
    (1) Edge on Windows attempts to play `.mov` files.
        However, it can't really play them.
        Thus if the browser is Edge on Windows, remove `.mov` files from sources.

    (2) Safari does not attempt to play (despite being capable of)
        `.mov` files that use the web-supported HEVC codec.
        To make it play such files, have to mark them as `video/mp4`.
        (Marking as `video/hevc` does not work atm.)

    (3) Safari attempts to play `.webm` files.
        However, it fails to correctly render transparency.
        Thus we ensure `.mov` files always precede `.webm` files.
  */
  let newSources: VideoSource[] = []
  let webmSources: VideoSource[] = []
  let movSources: VideoSource[] = []
  let otherSources: VideoSource[] = []

  for (let source of props.sources) {
    let newSource = { ...source }

    if (source.src.endsWith('.mov')) {
      newSource.type = 'video/mp4' // (2)
      movSources.push(newSource)
    } else if (source.src.endsWith('.webm')) {
      webmSources.push(newSource)
    } else {
      otherSources.push(newSource)
    }
  }

  // (1) & (3)
  if (!browserIsWindowsEdge.value) {
    newSources.push(...movSources)
  }
  newSources.push(...webmSources)
  newSources.push(...otherSources)

  return newSources
})

const checkBrowser = () => {
  let windows = false
  let edge = false

  if (navigator.userAgent.indexOf('Win') != -1) windows = true
  if (navigator.userAgent.indexOf('Edg') != -1) edge = true
  if (windows && edge) browserIsWindowsEdge.value = true
}

const setPlayingAsTrue = () => {
  playing = true
}
const setPlayingAsFalse = () => {
  playing = false
}

const lazyAutoplayInit = () => {
  if (props.autoplay && props.lazyAutoplay) {
    // Listen for visibility within page
    intersectionObserver = new IntersectionObserverWrapper(
      videoRef.value,
      {},
      lazyAutoplayOnIntersecting,
      lazyAutoplayOnNotIntersecting
    )
    intersectionObserver.init()

    // Listen for visibility of entire page
    pageVisibilityWrapper = new PageVisibilityWrapper(
      lazyAutoplayOnPageVisible,
      lazyAutoplayOnPageHidden
    )
    pageVisibilityWrapper.init()
  }
}

const lazyAutoplayOnVisible = () => {
  if (!videoRef.value) return

  if (props.loop || videoRef.value.currentTime < videoRef.value.duration) {
    videoRef.value.play()
  }
}

const lazyAutoplayOnHidden = () => {
  if (!videoRef.value) return

  if (playing) {
    videoRef.value.pause()
  }
}

const lazyAutoplayOnIntersecting = () => {
  videoVisible = true
  lazyAutoplayOnVisible()
}

const lazyAutoplayOnNotIntersecting = () => {
  videoVisible = false
  lazyAutoplayOnHidden()
}

const lazyAutoplayOnPageVisible = () => {
  if (videoVisible) {
    lazyAutoplayOnVisible()
  }
}

const lazyAutoplayOnPageHidden = () => {
  if (videoVisible) {
    lazyAutoplayOnHidden()
  }
}

const lazyAutoplayCleanup = () => {
  if (intersectionObserver) {
    intersectionObserver.destroy()
  }
  if (pageVisibilityWrapper) {
    pageVisibilityWrapper.destroy()
  }
}
</script>

<template>
  <component
    :is="computedWrapperTag"
    data-test="video-wrapper"
    :class="['video-wrapper', computedWrapperModifierClass]"
    :aspect-ratio="computedAspectRatio"
    :style="{
      width: width ? width : '100%',
      height: height ? height : 'auto'
    }"
  >
    <video
      ref="videoRef"
      class="video-wrapper__video"
      :autoplay="autoplay ? true : undefined"
      :controls="controls ? true : undefined"
      :loop="loop ? true : undefined"
      :muted="muted ? true : undefined"
      :playsinline="playsinline ? true : undefined"
      :poster="poster"
      :preload="preload"
      :src="src && sources.length <= 0 ? src : undefined"
    >
      <template v-if="sources.length > 0">
        <source
          v-for="(_source, index) in computedSources"
          :key="index"
          :src="_source.src"
          :type="_source.type"
        />
      </template>
    </video>
  </component>
</template>

<style lang="scss" scoped>
.video-wrapper {
  &__video {
    height: 100%;
    object-fit: v-bind(objectFit);
    object-position: v-bind(objectPosition);
    width: 100%;
  }
}
</style>
